import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const PATTERNS = {
    instagram: /instagram\.com\/(p|reel|tv|stories)\//i,
    tiktok: /(?:tiktok\.com\/@[\w.]+\/video\/|vm\.tiktok\.com\/|vt\.tiktok\.com\/)/i,
    youtube: /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)/i,
    twitter: /(?:twitter\.com|x\.com)\/\w+\/status\/\d+/i,
    facebook: /(?:www\.)?facebook\.com\//i,
};

const useInjectScript = (src) => {
    useEffect(() => {
        if (!src) return;
        if (document.querySelector(`script[src="${src}"]`)) return;
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        document.body.appendChild(script);
    }, [src]);
};

const detectPlatform = (link) => {
    for (const [platform, pattern] of Object.entries(PATTERNS)) {
        if (pattern.test(link)) return platform;
    }
    return null;
};

const extractYouTubeId = (link) => {
    const standard = link.match(/youtube\.com\/watch\?v=([\w-]+)/i);
    if (standard) return standard[1];
    const short = link.match(/youtu\.be\/([\w-]+)/i);
    if (short) return short[1];
    const shorts = link.match(/youtube\.com\/shorts\/([\w-]+)/i);
    if (shorts) return shorts[1];
    return null;
};

const extractTikTokVideoId = (link) => {
    const match = link.match(/\/video\/(\d+)/i);
    return match ? match[1] : null;
};

const EmbedPlaceholder = () => {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-center py-10 text-sm text-slate-400">
            <span className="animate-pulse">{t("CONTENT_EMBED_LOADING")}</span>
        </div>
    );
};

const EmbedError = () => {
    const { t } = useTranslation();
    return (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {t("CONTENT_VALIDATION_LINK_PLATFORM")}
        </div>
    );
};

const YouTubeEmbed = ({ link }) => {
    const id = extractYouTubeId(link);
    if (!id) return <EmbedError />;

    return (
        <div className="aspect-video w-full overflow-hidden rounded-xl">
            <iframe
                src={`https://www.youtube.com/embed/${id}`}
                title="YouTube post"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full border-0"
            />
        </div>
    );
};

const FacebookEmbed = ({ link }) => (
    <div className="overflow-hidden rounded-xl">
        <iframe
            src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(link)}&show_text=true&width=560`}
            width="100%"
            height="560"
            style={{ border: "none", overflow: "hidden" }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title="Facebook post"
            className="rounded-xl"
        />
    </div>
);

const InstagramEmbed = ({ link }) => {
    useInjectScript("//www.instagram.com/embed.js");
    const ref = useRef(null);

    useEffect(() => {
        const instagramWindow = window;
        if (instagramWindow?.instgrm?.Embeds) {
            instagramWindow.instgrm.Embeds.process();
        }
    }, [link]);

    return (
        <div ref={ref} className="flex justify-center rounded-xl">
            <blockquote
                className="instagram-media"
                data-instgrm-permalink={link}
                data-instgrm-version="14"
                style={{ maxWidth: "540px", width: "100%" }}
            >
                <EmbedPlaceholder label="Instagram" />
            </blockquote>
        </div>
    );
};

const TwitterEmbed = ({ link }) => {
    useInjectScript("https://platform.twitter.com/widgets.js");
    const ref = useRef(null);

    useEffect(() => {
        const twitterWindow = window;
        if (twitterWindow?.twttr?.widgets) {
            twitterWindow.twttr.widgets.load(ref.current);
        }
    }, [link]);

    return (
        <div ref={ref} className="flex justify-center rounded-xl">
            <blockquote className="twitter-tweet" data-dnt="true">
                <a href={link}>{link}</a>
            </blockquote>
        </div>
    );
};

const TikTokEmbed = ({ link }) => {
    const { t } = useTranslation();
    useInjectScript("https://www.tiktok.com/embed.js");
    const videoId = extractTikTokVideoId(link);
    if (!videoId) return (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-500 min-w-0">
                <svg className="h-4 w-4 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.27 8.27 0 0 0 4.84 1.54V6.79a4.85 4.85 0 0 1-1.07-.1z"/>
                </svg>
                <span className="truncate text-xs">{link}</span>
            </div>
            <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="ml-3 shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 focus:outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                {t("CONTENT_VIEW_LINK")}
            </a>
        </div>
    );

    return (
        <div className="flex justify-center rounded-xl">
            <blockquote
                className="tiktok-embed"
                cite={link}
                data-video-id={videoId}
                style={{ maxWidth: "605px", minWidth: "0" }}
            >
                <section>
                    <EmbedPlaceholder label="TikTok" />
                </section>
            </blockquote>
        </div>
    );
};

const PostEmbed = ({ link }) => {
    const normalizedLink = String(link || "").trim();
    if (!normalizedLink) return null;

    const platform = detectPlatform(normalizedLink);
    if (!platform) return <EmbedError />;

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
            {platform === "youtube" && <YouTubeEmbed link={normalizedLink} />}
            {platform === "facebook" && <FacebookEmbed link={normalizedLink} />}
            {platform === "instagram" && <InstagramEmbed link={normalizedLink} />}
            {platform === "twitter" && <TwitterEmbed link={normalizedLink} />}
            {platform === "tiktok" && <TikTokEmbed link={normalizedLink} />}
        </div>
    );
};

export default PostEmbed;
